/**
 * lib/env/EnvironmentBadge.tsx — indicador de entorno + BLOQUEO total por cruce.
 *
 * Badge (sólo DEV):
 *   🟡 QA · qa_local · ledger 00227 · analytics ✓
 *   🔴 PRINCIPAL · principal_local · ledger 00210 · analytics no desplegada
 * El ledger mostrado SIEMPRE proviene del servidor; si no se pudo verificar se dice
 * "ledger no verificado" (nunca se presenta VITE_EXPECTED_LEDGER como si fuera real).
 *
 * EnvironmentGuard bloquea TODA la app (no sólo avisa) cuando la identidad no cuadra.
 */

import type { ReactNode } from 'react'
import { useEnvironment } from './envContext'

export function EnvironmentBadge() {
  const { env, analytics } = useEnvironment()
  if (!import.meta.env.DEV) return null

  const dot = env.kind === 'qa' ? '🟡' : env.kind === 'principal' ? '🔴' : '⚪'
  const ring =
    env.color === 'amber'
      ? 'border-amber-400 bg-amber-50 text-amber-900'
      : env.color === 'red'
        ? 'border-red-400 bg-red-50 text-red-900'
        : 'border-gray-400 bg-gray-50 text-gray-800'

  // Ledger REAL del servidor; nunca el esperado por configuración.
  const ledger =
    'ledger' in analytics && analytics.ledger
      ? `ledger ${analytics.ledger}`
      : 'ledger no verificado'
  const rawId = 'environmentId' in analytics ? analytics.environmentId : null

  // 4 estados de IDENTIDAD distinguidos — una identidad negativa NUNCA se presenta como
  // verificación positiva:
  //   verified     → environment_id conocido (qa_local/principal_local)
  //   absent       → RPC ausente (PGRST202, status 'absent') — principal pristino 00210 (compat)
  //   sin verificar→ sonda en curso / sin red / sin sesión / ERROR de verificación
  //   no config.   → RPC presente pero environment_id='unknown' (cadena sin bootstrap → bloqueo)
  const identity: string =
    analytics.status === 'absent'
      ? 'identidad ausente'
      : rawId && rawId !== 'unknown'
        ? `id ${rawId} ✓`
        : analytics.status === 'loading' ||
            analytics.status === 'offline' ||
            analytics.status === 'forbidden' ||
            analytics.status === 'error'
          ? 'identidad sin verificar'
          : 'identidad no configurada'

  const tag =
    analytics.status === 'available'
      ? 'analytics ✓'
      : analytics.status === 'unavailable' || analytics.status === 'absent'
        ? 'analytics no desplegada'
        : analytics.status === 'incompatible'
          ? 'analytics incompatible'
          : analytics.status === 'loading'
            ? '…'
            : analytics.status

  return (
    <div
      className={`fixed bottom-2 left-2 z-[9999] rounded-lg border px-2.5 py-1 font-mono text-[11px] font-semibold shadow-sm ${ring}`}
      title={`${env.origin}`}
    >
      {dot} {env.label} · {identity} · {ledger} · {tag}
    </div>
  )
}

/**
 * Bloqueo TOTAL: ante cruce de entorno / host no permitido / identidad no verificable,
 * la app no se renderiza (no basta un badge rojo).
 */
export function EnvironmentGuard({ children }: { children: ReactNode }) {
  const { env, block, refreshCapability } = useEnvironment()
  if (!block) return <>{children}</>

  const title =
    block.kind === 'cross-env'
      ? '⛔ Entorno cruzado — aplicación bloqueada'
      : block.kind === 'host'
        ? '⛔ Destino no permitido — aplicación bloqueada'
        : '⛔ Entorno no verificado — aplicación bloqueada'

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-red-950/95 p-6">
      <div className="max-w-md rounded-2xl border-2 border-red-500 bg-white p-6 shadow-2xl">
        <h1 className="text-[16px] font-black text-red-700">{title}</h1>
        <p className="mt-2 text-[13px] text-navy-700">{block.detail}</p>
        <p className="mt-3 text-[12px] text-surface-500">
          Modo solicitado: <strong>{env.label}</strong> · destino <strong>{env.origin}</strong>
        </p>
        <p className="mt-3 text-[12px] text-surface-500">
          Corrige la configuración del entorno y reinicia con <code>pnpm dev:qa</code> o{' '}
          <code>pnpm dev:principal</code>. No se permite operar en este estado.
        </p>
        <button
          onClick={refreshCapability}
          className="mt-4 rounded-lg border border-red-400 px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-50"
        >
          Reverificar
        </button>
      </div>
    </div>
  )
}
